import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";
import apiServices from "../../services/service";
import FormModalWrapper from "../../shared/modal/FormModalWrapper";
import SearchableDropDown from "../../shared/controls/form/searchableDropdown";
import DateBox from "../../shared/controls/form/dateBox";

const ACCESS_LEVEL_OPTIONS = [
  { value: "L1", label: "L1" },
  { value: "L2", label: "L2" },
  { value: "L3", label: "L3" },
];

const ACCESS_LEVEL_RANK = { L1: 1, L2: 2, L3: 3 };

const BUCKET_VISIBILITY = [
  { value: "Public", label: "Public" },
  { value: "Private", label: "Private" },
]

const toDateOnlyString = (d) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

const getTodayString = () => toDateOnlyString(new Date());

const getMaxExpiresAtString = () => {
  const d = new Date();
  d.setFullYear(d.getFullYear() + 1);
  return toDateOnlyString(d);
};

const AddBucketForm = ({ open, onClose, onSave, usersList = [], editData = null }) => {
  const isEdit = !!editData;
  const [bucketName, setBucketName] = useState("");
  const [owner, setOwner] = useState(null);
  const [accessLevel, setAccessLevel] = useState(null);
  const [visibility, setVisibility] = useState(null);
  const [assignedUsers, setAssignedUsers] = useState([]);
  const [expiresAt, setExpiresAt] = useState(getMaxExpiresAtString);
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);

  const userOptions = usersList.map((u) => ({
    value: u.userId,
    label:
      `${u.firstName || ""} ${u.lastName || ""}`.trim() ||
      u.userName ||
      u.emailAddress ||
      u.email ||
      "",
    accessLevel: u.accessLevel,
  }));

  const isPrivate = visibility?.value === "Private";

  // Only users at or above the bucket's access level can own it or be granted private access.
  const accessLevelRank = ACCESS_LEVEL_RANK[accessLevel?.value] ?? null;
  const assignableUserOptions = accessLevelRank
    ? userOptions.filter((u) => ACCESS_LEVEL_RANK[u.accessLevel] >= accessLevelRank)
    : userOptions;

  const reset = () => {
    setBucketName("");
    setOwner(null);
    setAccessLevel(null);
    setVisibility(null);
    setAssignedUsers([]);
    setExpiresAt(getMaxExpiresAtString());
    setErrors({});
  };

  // Populate fields from the bucket being edited, or reset to blank defaults for a fresh Add
  useEffect(() => {
    if (!open) return;
    if (editData) {
      setBucketName(editData.name || "");
      setOwner(
        userOptions.find((o) => String(o.value) === String(editData.bucketOwnerId)) ||
          (editData.bucketOwnerId
            ? { value: editData.bucketOwnerId, label: editData.bucketOwnerFullName || editData.bucketOwnerId }
            : null)
      );
      setAccessLevel(ACCESS_LEVEL_OPTIONS.find((o) => o.value === editData.accessLevel) || null);
      setVisibility(BUCKET_VISIBILITY.find((o) => o.value === editData.bucketVisibility) || null);
      // Private buckets carry their current grants as a `users` array ({ userId, userFullName, ... })
      setAssignedUsers(
        (editData.users || []).map((u) => {
          const match = userOptions.find((o) => String(o.value) === String(u.userId));
          return match || { value: u.userId, label: u.userFullName || u.userId };
        })
      );
      setExpiresAt(
        editData.expiryDateTime ? toDateOnlyString(new Date(editData.expiryDateTime)) : getMaxExpiresAtString()
      );
    } else {
      reset();
    }
    setErrors({});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, editData]);

  const handleClose = () => {
    reset();
    onClose();
  };

  const handleSave = () => {
    const newErrors = {};
    if (!bucketName.trim()) newErrors.bucketName = "Bucket name is required";
    if (!owner) newErrors.owner = "Bucket owner is required";
    if (!accessLevel) newErrors.accessLevel = "Access level is required";
    if (!visibility) newErrors.visibility = "Bucket visibility is required";
    if (visibility?.value === "Private" && !assignedUsers.length)
      newErrors.assignedUsers = "Please assign at least one user";
    if (!expiresAt) newErrors.expiresAt = "Expiry date is required";
    else if (expiresAt < getTodayString() || expiresAt > getMaxExpiresAtString())
      newErrors.expiresAt = "Expiry date must be between today and one year from today";
    if (Object.keys(newErrors).length) {
      setErrors(newErrors);
      return;
    }

    const basePayload = {
      name: bucketName.trim(),
      bucketOwnerId: owner?.value ?? null,
      accessLevel: accessLevel?.value ?? null,
      bucketVisibility: visibility?.value ?? null,
      expiryDateTime: expiresAt,
    };

    setSaving(true);

    let request;
    if (isEdit) {
      // Bucket fields go through Update; private-bucket membership goes through its own
      // grant endpoint in parallel (Update never carries userIds).
      const updateRequest = apiServices.updateBucket({ id: editData.id, ...basePayload });
      request =
        visibility?.value === "Private"
          ? Promise.all([
              updateRequest,
              apiServices.updateBucketMembers({
                bucketId: editData.id,
                userIds: assignedUsers.map((u) => u.value).join(","),
              }),
            ]).then(([updateResponse]) => updateResponse)
          : updateRequest;
    } else {
      request = apiServices.addBucket({
        ...basePayload,
        userIds: visibility?.value === "Private" ? assignedUsers.map((u) => u.value).join(",") : null,
      });
    }

    request
      .then((response) => {
        // Refetch so we hand the parent the actual saved record (owner name, users, etc.)
        // rather than whatever Update's own response happens to echo back.
        if (isEdit) {
          return apiServices.getBuckets().then((listResponse) => {
            const raw = listResponse.data;
            const list = Array.isArray(raw) ? raw : raw?.bucketList || raw?.buckets || raw?.data || [];
            return list.find((b) => String(b.id) === String(editData.id)) || response.data;
          });
        }
        return response.data;
      })
      .then((data) => {
        toast.success(isEdit ? "Bucket updated successfully!" : "Bucket added successfully!", { position: "top-right" });
        onSave(data);
        reset();
      })
      .catch((error) => {
        toast.error(
          error?.response?.data?.message ||
            error.message ||
            (isEdit ? "Failed to update bucket" : "Failed to add bucket"),
          { position: "top-right" }
        );
      })
      .finally(() => setSaving(false));
  };

  return (
    <FormModalWrapper
      open={open}
      onClose={handleClose}
      breadcrumb="Bucket Management"
      title={isEdit ? "Edit Bucket" : "Add Bucket"}
      size="sm"
      onSave={handleSave}
      saveDisabled={saving}
    >
      <div className="row">
        <div className="col-12">
          <div className="input-container">
            <label htmlFor="bucketName">
              Bucket Name <span className="required-star">*</span>
            </label>
            <input
              id="bucketName"
              className="formik-input"
              type="text"
              value={bucketName}
              onChange={(e) => {
                setBucketName(e.target.value);
                if (errors.bucketName) setErrors((prev) => ({ ...prev, bucketName: undefined }));
              }}
              placeholder="Enter bucket name"
            />
            {errors.bucketName && (
              <div className="error-message">{errors.bucketName}</div>
            )}
          </div>
        </div>
        <div className="col-12">
          <div className="input-container">
            <SearchableDropDown
              label="Access Level"
              name="accessLevel"
              fieldName="accessLevel"
              value={accessLevel}
              placeholder="Select access level"
              options={ACCESS_LEVEL_OPTIONS}
              isRequired
              error={!!errors.accessLevel}
              errormsg={errors.accessLevel}
              onChange={(selected) => {
                setAccessLevel(selected);
                if (errors.accessLevel) setErrors((prev) => ({ ...prev, accessLevel: undefined }));

                // Drop the owner and any assigned users who no longer meet the new access level's floor
                const newRank = ACCESS_LEVEL_RANK[selected?.value] ?? null;
                if (newRank) {
                  setOwner((prev) => (prev && ACCESS_LEVEL_RANK[prev.accessLevel] >= newRank ? prev : null));
                  setAssignedUsers((prev) =>
                    prev.filter((u) => ACCESS_LEVEL_RANK[u.accessLevel] >= newRank)
                  );
                }
              }}
            />
          </div>
        </div>

        <div className="col-12">
          <div className="input-container">
            <DateBox
              label="Expires At"
              name="expiresAt"
              isRequired
              value={expiresAt}
              minDate={getTodayString()}
              maxDate={getMaxExpiresAtString()}
              format="MM-dd-yyyy"
              placeholder="Select expiry date"
              errormsg={errors.expiresAt}
              handleChange={(e) => {
                setExpiresAt(e.target.value);
                if (errors.expiresAt) setErrors((prev) => ({ ...prev, expiresAt: undefined }));
              }}
            />
          </div>
        </div>

        <div className="col-12">
          <div className="input-container">
            <SearchableDropDown
              label="Bucket Owner"
              name="owner"
              fieldName="owner"
              value={owner}
              placeholder="Select owner"
              options={assignableUserOptions}
              isRequired
              isDisabled={isEdit}
              error={!!errors.owner}
              errormsg={errors.owner}
              onChange={(selected) => {
                setOwner(selected);
                if (errors.owner) setErrors((prev) => ({ ...prev, owner: undefined }));
              }}
            />
          </div>
        </div>

        <div className="col-12">
          <div className="input-container">
            <SearchableDropDown
              label="Bucket Visibility"
              name="visibility"
              fieldName="visibility"
              value={visibility}
              placeholder="Select bucket visibility"
              options={BUCKET_VISIBILITY}
              isRequired
              isDisabled={isEdit}
              error={!!errors.visibility}
              errormsg={errors.visibility}
              onChange={(selected) => {
                setVisibility(selected);
                if (errors.visibility) setErrors((prev) => ({ ...prev, visibility: undefined }));
                if (selected?.value !== "Private") {
                  setAssignedUsers([]);
                  if (errors.assignedUsers) setErrors((prev) => ({ ...prev, assignedUsers: undefined }));
                }
              }}
            />
          </div>
        </div>

        {isPrivate && (
          <div className="col-12">
            <div className="input-container">
              <SearchableDropDown
                label="Assign User"
                name="assignedUsers"
                fieldName="assignedUsers"
                value={assignedUsers}
                placeholder="Select users"
                options={assignableUserOptions}
                isMulti
                isRequired
                error={!!errors.assignedUsers}
                errormsg={errors.assignedUsers}
                onChange={(selected) => {
                  setAssignedUsers(selected || []);
                  if (errors.assignedUsers) setErrors((prev) => ({ ...prev, assignedUsers: undefined }));
                }}
              />
            </div>
          </div>
        )}
      </div>
    </FormModalWrapper>
  );
};

export default AddBucketForm;
